import { useContext, useEffect, useState } from "react";
import { Button, Form, Select, Row, Col, Input } from "antd";
import { FormContent, FormFooter } from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { TDropdownOptions, TResponseModel, TUser } from "../../../types/config";
import {
  eHTTPStatusCode,
  ePrivileges,
  eResultCode,
  eSERVICEREQUEST,
  eTICKETTYPE,
} from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import TextArea from "antd/es/input/TextArea";
import { useNavigate } from "react-router-dom";
import styles from "./ticket.module.css";
import CustomDropdown from "../../../components/custom-dropdown/custom-dropdown";
import {
  AddEditTicket,
  GetConfigParamDataList,
  GetCustomerList,
  GetDealerList,
  GetSpecificDealerData,
  GetSpecificTicket,
  GetTicketProgressReport,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import {
  CONFIG_GROUPS,
  STAKE_HOLDERS,
  UNAUTHORIZEDREQUEST,
} from "../../../utils/constants";

import {
  TDealer,
  TSpareDependency,
  TTicket,
  TTicketDetail,
} from "../../../types";
import "./ticket.module.css";
import dayjs from "dayjs";
import { UserContext } from "../../../context";
import DealerDetails from "./dealer-details";
import CustomModal from "../../../components/modal";
import NewDealerForm from "./new-dealer-form";
import {
  CloseOutlined,
  FilePdfOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { requiredFieldValidator } from "../../../utils/input.validator";
import AuthUtil from "../../../utils/auth";

export default function TicketForm() {
  const newUrl = new URL(window.location.href);
  const queryParams = newUrl.searchParams;

  const [ticketForm] = Form.useForm();
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const navigate = useNavigate();
  const { onShowToast } = useToast();

  const [issueCategoryOptions, setIssueCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [ticketType, setTicketType] = useState<number | null>();
  const [issueCategory, setIssueCategory] = useState<number | null>();
  const [ticketId, setTicketId] = useState<number>(
    queryParams.get("id") || (0 as any)
  );
  const [selectedDealer, setSelectedDealer] = useState<TDealer | null>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [ticketDetails, setTicketDetails] = useState<TTicket | null>(null);
  const [spareDetails, setSpareDetails] = useState<TSpareDependency[]>([]);
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [isTextCopied, setTextCopied] = useState<boolean>(false);
  const [serviceReqNo, setServiceReqNo] = useState<string>("");

  // Hook to handle on load operations
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const issueCategory = await fetchConfigParams(
        CONFIG_GROUPS.ISSUECATEGORY
      );
      setIssueCategoryOptions(issueCategory);
      setIsLoading(false);
    };

    fetchData(); // Call the fetch function
  }, []);

  // To fetch specific config param
  const fetchSpecificDealer = async (id: number) => {
    try {
      const payload = {
        data: {
          id: id,
        },
      };

      const response: TResponseModel = await post(
        GetSpecificDealerData,
        payload
      );
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const {
            installationDate,
            amcFromDate,
            amcToDate,
            customerName,
            isAMCActive,
            lastPM,
            warrantyDate,
            ...restData
          } = data;
          setSelectedDealer({
            ...restData,
            installationDate: installationDate ? dayjs(installationDate) : null,
            amcFromDate: amcFromDate ? dayjs(amcFromDate) : null,
            amcToDate: amcToDate ? dayjs(amcToDate) : null,
            lastPM: lastPM ? dayjs(lastPM) : null,
            warrantyDate: warrantyDate ? dayjs(warrantyDate) : null,
            customerName: customerName,
            isAMCActive,
          });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // To get the list of parameter options
  const fetchConfigParams = async (groupUniqueId: string) => {
    try {
      const payload = {
        data: {
          groupUniqueId,
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          return Utils.mapDropdownOptions(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // Hook to handle on load operations
  useEffect(() => {
    fetchSpecificTicket(ticketId);
  }, [ticketId]);

  useEffect(() => {
    setActiveUser(context?.user);
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      ticketForm.setFieldValue("customerId", context?.user?.orgId);
      ticketForm.setFieldValue("customerName", context?.user?.orgName);
      setSelectedCustomer(context?.user?.orgId);
    }
  }, [context?.user]);

  // Handle on submit event
  const onFinish = async (values: TTicket) => {
    const { servicePartnerId, ...restValues } = values;
    try {
      const payload = {
        data: {
          id: 0,
          sla: selectedDealer?.sla,
          servicePartnerId: selectedDealer?.servicePartnerId,
          ...restValues,
        },
      };
      setIsLoading(true);

      const response: TResponseModel = await post(AddEditTicket, payload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        // onShowToast({
        //   type: ToastType.success,
        //   title: <CheckOutlined />,
        //   position: ToastOpen.centerTop,
        //   content: response.dataResponse.description,
        // });
        setServiceReqNo(data?.ServiceRequestNo);
        setModalVisible(true);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificTicket = async (id: number) => {
    if (id) {
      try {
        const payload = {
          data: {
            id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(GetSpecificTicket, payload);
        const { data, dataResponse } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          const {
            customerId,
            dealerDetails,
            ticketDetails,
            ticketType,
            issueCatId,
            ...restData
          } = data;
          const { id, dealerName, ...restDetails } = dealerDetails;
          fetchSpecificDealer(id);

          setSelectedCustomer(customerId);
          setTicketType(ticketType);
          setIssueCategory(issueCatId);
          ticketForm.setFieldsValue({ ...data, dealerId: id, dealerName });
          setTicketDetails({ ...data, dealerId: id, dealerName });

          const consolidatedSpareDetails = (
            data as TTicket
          ).ticketDetailList.flatMap(
            (ticketDetail: TTicketDetail) =>
              ticketDetail.spareDependency?.spareDetails || []
          );

          setSpareDetails(consolidatedSpareDetails as any);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const fetchNewDealerDetails = (dealer: TDealer) => {
    ticketForm.setFieldValue("dealerId", dealer.id);
    ticketForm.setFieldValue("dealerName", dealer.dealerName);

    fetchSpecificDealer(dealer?.id as number);
  };

  const generateInvoicePdf = async (ticketDetails: TTicket) => {
    try {
      // Start loading
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetTicketProgressReport}`,
        {
          method: "POST", // Change to POST
          headers: {
            "Content-Type": "application/json", // Set content type for JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              id: ticketDetails.id,
            },
          }),
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = `${ticketDetails.serviceRequestNo}.pdf`; // Set the desired file name with .pdf extension

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "Ticket progress report downloaded successfully",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Error while downloading file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading PDF file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  // const generateInvoicePdf = async (
  //   ticketDetails: TTicket,
  //   selectedDealer: TDealer
  // ) => {
  //   try {
  //     // Fetch the HTML template
  //     const response = await fetch("/assets/templates/ticketHtmlTemplate.html");
  //     let htmlTemplate = await response.text();

  //     const ticketTypeNameReplacement =
  //       ticketDetails?.ticketType === eSERVICEREQUEST.OTHER
  //         ? ticketDetails?.otherTicketTypeList
  //             ?.map((item) => item.label)
  //             .join(", ")
  //         : ticketDetails?.ticketTypeName;

  //     const issueCatOneList = ticketDetails?.subIssueCatTwoList
  //       ?.map((item) => item.name)
  //       .join(", ");

  //     const issueCatTwoList = ticketDetails?.subIssueCatTwoList
  //       ?.map((item) => item.name)
  //       .join(", ");

  //     const closingMode =
  //       ticketDetails?.closingMode === eTICKETCLOSINGMODE.INPERSONSUPPORT
  //         ? "In Person"
  //         : "Online";
  //     // Replace placeholders directly in the HTML template
  //     htmlTemplate = htmlTemplate
  //       .replace(
  //         /@CLOSING_MODE@/g,
  //         ` <div class="area">
  //               <div>
  //                   <label>Closing Mode</label>
  //                   <p>${closingMode || "-"}</p>
  //               </div>
  //               <div>
  //                   <label>Closing Comment</label>
  //                   <p>${ticketDetails.closingComment}</p>
  //               </div>
  //           </div>`
  //       )
  //       .replace(
  //         /@ISSUE_CATEGORY@/g,
  //         ` <div class="area">
  //               <div>
  //                   <label>Sub Issue Category One</label>
  //                   <p>${issueCatOneList || "-"}</p>
  //               </div>
  //               <div>
  //                   <label>Sub Issue Category Two</label>
  //                   <p>${issueCatTwoList || "-"}</p>
  //               </div>
  //           </div>`
  //       )
  //       .replace(/@serviceRequestNo@/g, ticketDetails?.serviceRequestNo || "-")
  //       .replace(/@customerName@/g, ticketDetails?.customerName || "-")
  //       .replace(/@ticketTypeName@/g, ticketTypeNameReplacement || "-")
  //       .replace(
  //         /@dealerName@/g,
  //         ticketDetails?.dealerDetails?.dealerName || "-"
  //       )
  //       .replace(/@custTicketNo@/g, ticketDetails?.custTicketNo || "-")
  //       .replace(/@issueCatName@/g, ticketDetails?.issueCatName || "-")
  //       .replace(
  //         /@currentStatusName@/g,
  //         ticketDetails?.currentStatusName?.toLocaleUpperCase() || "-"
  //       )
  //       .replace(
  //         /@ticketCreationDate@/g,
  //         ticketDetails?.ticketCreationDate
  //           ? DateUtils.format(
  //               ticketDetails?.ticketCreationDate,
  //               dateOnlyFormat
  //             )
  //           : "-"
  //       )
  //       .replace(/@issueDescription@/g, ticketDetails?.issueDescription || "-")
  //       .replace(/@remarks@/g, ticketDetails?.remarks || "-")
  //       .replace(/@dealerCode@/g, selectedDealer?.dealerCode || "-")
  //       .replace(/@depotCode@/g, selectedDealer?.depotCode || "-")
  //       .replace(/@regionName@/g, selectedDealer?.regionName || "")
  //       .replace(/@districtName@/g, selectedDealer?.districtName || "-")
  //       .replace(/@zipcode@/g, selectedDealer?.zipcode || "-")
  //       .replace(/@stateName@/g, selectedDealer?.stateName || "")
  //       .replace(/@locationTypeName@/g, selectedDealer?.locationTypeName || "-")
  //       .replace(/@contactName@/g, selectedDealer?.contactName || "-")
  //       .replace(/@contactNo@/g, selectedDealer?.contactNo || "-")
  //       .replace(/@dealerEmail@/g, selectedDealer?.dealerEmail || "-")
  //       .replace(
  //         /@servicePartnerName@/g,
  //         selectedDealer?.servicePartnerName || "-"
  //       )
  //       .replace(/@address@/g, selectedDealer?.address || "-")
  //       .replace(/@sla@/g, `${selectedDealer?.sla} hrs` || "-")
  //       .replace(/@machineTypeName@/g, selectedDealer?.machineTypeName || "-")
  //       .replace(/@productName@/g, selectedDealer?.productName || "-")
  //       .replace(/@machineSerialNo@/g, selectedDealer?.machineSerialNo || "-")
  //       .replace(
  //         /@installationDate@/g,
  //         selectedDealer?.installationDate
  //           ? DateUtils.format(selectedDealer?.installationDate, dateOnlyFormat)
  //           : "-"
  //       )
  //       .replace(/@gyro@/g, selectedDealer?.gyro ? "Yes" : "No")
  //       .replace(/@gyroSerialNo@/g, selectedDealer?.gyroSerialNo || "-")
  //       .replace(
  //         /@warrantyDate@/g,
  //         selectedDealer?.warrantyDate
  //           ? DateUtils.format(selectedDealer?.warrantyDate, dateOnlyFormat)
  //           : "-"
  //       )
  //       .replace(
  //         /@amcFromDate@/g,
  //         selectedDealer?.amcFromDate
  //           ? DateUtils.format(selectedDealer?.amcFromDate, dateOnlyFormat)
  //           : "-"
  //       )
  //       .replace(
  //         /@amcToDate@/g,
  //         selectedDealer?.amcToDate
  //           ? DateUtils.format(selectedDealer?.amcToDate, dateOnlyFormat)
  //           : "-"
  //       )
  //       .replace(
  //         /@lastPM@/g,
  //         selectedDealer?.lastPM
  //           ? DateUtils.format(selectedDealer?.lastPM, dateOnlyFormat)
  //           : "-"
  //       )
  //       .replace(
  //         /@isActive@/g,
  //         selectedDealer?.isActive ? "Active" : "Inactive"
  //       );

  //     const generateTable = (
  //       ticketDetails: TTicketDetail[],
  //       startIndex: number
  //     ) => {
  //       const serviceTable = ticketDetails
  //         ?.map(
  //           (item, index) =>
  //             `<tr>
  //       <td>${startIndex + index + 1}</td>
  //       <td>${item.updatedByName}</td>
  //       <td>${item.description}</td>
  //        <td>${DateUtils.format(item.createdOn, dateFormat)}</td>
  //       <td>${item.statusName}</td>
  //       </tr>`
  //         )
  //         .join("");
  //       return serviceTable;
  //     };

  //     const start = 0;
  //     const end = ticketDetails?.ticketDetailList.length || 0;

  //     const paginatedItems: TTicketDetail[] =
  //       ticketDetails?.ticketDetailList.slice(start, end) || [];
  //     const serviceTable = generateTable(paginatedItems, start);

  //     htmlTemplate = htmlTemplate.replace("@ITEMS@", serviceTable);

  //     // Wrap the final HTML
  //     const finalHtml = `
  //       <html>
  //         <head>
  //         </head>
  //         <body>
  //           <div>${htmlTemplate}</div>
  //         </body>
  //       </html>
  //     `;

  //     // Define PDF options
  //     const opt = {
  //       filename: `${ticketDetails?.serviceRequestNo || "Invoice"}-report.pdf`,
  //       image: { type: "jpeg", quality: 0.2 },
  //       html2canvas: { scale: 4, useCORS: true },
  //       jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
  //     };

  //     // Generate PDF
  //     await html2pdf().from(finalHtml).set(opt).save();

  //     console.log(`PDF Downloaded Successfully`);
  //   } catch (error) {
  //     console.error("Error generating invoice PDF:", error);
  //   }
  // };

  return (
    <>
      <div className={styles.formPadding}>
        <CustomModal
          isFullScreen={true}
          title={"New Dealer Details"}
          useCustomHeader={true}
          isOpen={isModalOpen}
          onCancel={handleCancel}
          hideOkButton={true}
          hideCancelButton={true}
          onOk={() => {}}
          footer={null}
        >
          <NewDealerForm
            fetchNewDealerDetails={fetchNewDealerDetails}
            ticketType={ticketType}
            selectedCustomer={selectedCustomer}
            onClose={handleCancel}
          ></NewDealerForm>
        </CustomModal>
        <div style={{ display: "flex", gap: "1vw" }}>
          {!ticketId ? (
            <div
              style={{
                width: "34.5vw",
                border: "1px solid #f4f4f4",
                height: "100vh",
              }}
            >
              <Form
                form={ticketForm}
                name="ticketForm"
                autoComplete="off"
                onFinish={onFinish}
                disabled={ticketId ? true : false}
                // initialValues={defaultValues}
                layout="vertical"
              >
                <FormContent className={styles.contentStyle}>
                  <Row gutter={16} className="formRowHeight">
                    <Col span={24}>
                      <Form.Item
                        name="customerId"
                        label={
                          <span className={styles.customLabel}>Customer</span>
                        }
                        rules={[
                          { required: true, message: "Please select customer" },
                        ]}
                      >
                        <CustomDropdown
                          isDisabled={
                            activeUser?.stakeHolderName ===
                              STAKE_HOLDERS.CUSTOMER || ticketId
                              ? true
                              : false
                          }
                          endPoint={GetCustomerList}
                          value={ticketForm.getFieldValue("customerId")}
                          selectedName={ticketForm.getFieldValue(
                            "customerName"
                          )}
                          onChange={(selected: any) => {
                            ticketForm.setFieldValue(
                              "customerId",
                              selected.value
                            );
                            ticketForm.setFieldValue(
                              "customerName",
                              selected.label
                            );
                            ticketForm.setFieldsValue({
                              dealerId: null,
                              dealerName: "",
                              issueCatId: null,
                              issueDescription: "",
                              ticketType: null,
                            });
                            setSelectedCustomer(selected.value);
                            setSelectedDealer(null);
                            setTicketType(null);
                            setIssueCategory(null);
                          }}
                          label=""
                          placeHolder="Select customer"
                        ></CustomDropdown>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="ticketType"
                        label={
                          <span className={styles.customLabel}>
                            Ticket Type
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please select ticket type",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select ticket type"
                          options={[
                            {
                              value: eSERVICEREQUEST.NEWINSTALLATION,
                              label: "New Installation",
                            },
                            {
                              value: eSERVICEREQUEST.SERVICEREQUEST,
                              label: "Service Request",
                            },
                          ]}
                          onChange={(value) => {
                            // Reset dependent fields
                            ticketForm.setFieldValue("ticketType", value);
                            setTicketType(value);

                            if (value === eTICKETTYPE.NEWINSTALLATION) {
                              ticketForm.setFieldValue("issueCatId", null); // Clear issueCatId field
                              ticketForm.setFieldValue(
                                "issueDescription",
                                null
                              ); // Clear issueDescription field
                            }
                            ticketForm.setFieldValue("dealerId", null); // Reset dealerId
                            setSelectedDealer(null);
                            setIssueCategory(null);
                          }}
                        ></Select>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="dealerId"
                        label={
                          <div className={styles.dealerWrapper}>
                            <div>
                              <span className={styles.customLabel}>Dealer</span>
                            </div>
                            <div>
                              {selectedCustomer &&
                              ticketType &&
                              ticketId === 0 &&
                              Utils.isUserHasAccess(
                                context.user?.privilegeList,
                                ePrivileges.ADDDEALERS
                              ) ? (
                                <span
                                  className={styles.addNewSpan}
                                  onClick={() => setIsModalOpen(true)}
                                >
                                  Add New
                                </span>
                              ) : null}
                            </div>
                          </div>
                        }
                        rules={[
                          { required: true, message: "Please select dealer" },
                        ]}
                      >
                        <CustomDropdown
                          isDisabled={
                            ticketId ||
                            ticketType === eTICKETTYPE.NEWINSTALLATION
                              ? true
                              : false
                          }
                          optionName="dealerName"
                          endPoint={GetDealerList}
                          value={ticketForm.getFieldValue("dealerId")}
                          selectedName={ticketForm.getFieldValue("dealerName")}
                          onChange={(selected: any) => {
                            // setDealerSelected(true);
                            ticketForm.setFieldValue(
                              "dealerId",
                              selected.value
                            );
                            ticketForm.setFieldValue(
                              "dealerName",
                              selected.label
                            );

                            fetchSpecificDealer(selected.value);
                          }}
                          label=""
                          placeHolder="Select dealer"
                          addPaylod={{ customerId: selectedCustomer }}
                        ></CustomDropdown>
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name="custTicketNo"
                        label={
                          <span className={styles.customLabel}>
                            Customer Ticket No
                          </span>
                        }
                      >
                        <Input placeholder="Enter customer ticket no." />
                      </Form.Item>
                    </Col>

                    {selectedDealer?.id &&
                    ticketType === eTICKETTYPE.SERVICEREQUEST ? (
                      <Col span={24}>
                        <Form.Item
                          dependencies={["dealerId", "ticketType"]}
                          name="issueCatId"
                          label={
                            <span className={styles.customLabel}>
                              Issue Category
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please select issue category",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select issue category"
                            options={issueCategoryOptions}
                            onChange={(value) => {
                              setIssueCategory(value);
                            }}
                          ></Select>
                        </Form.Item>
                      </Col>
                    ) : null}
                    {issueCategory ? (
                      <Col span={24}>
                        <Form.Item
                          name="issueDescription"
                          label={
                            <span className={styles.customLabel}>
                              Issue Description
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              validator: requiredFieldValidator(
                                "enter issue description"
                              ),
                            },
                          ]}
                        >
                          <TextArea
                            rows={4}
                            placeholder="Enter the comment..."
                            maxLength={500}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}

                    <Col span={24}>
                      <Form.Item
                        name="remarks"
                        label={
                          <span className={styles.customLabel}>Remarks</span>
                        }
                      >
                        <TextArea
                          rows={4}
                          placeholder="Enter the comment..."
                          maxLength={500}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </FormContent>

                <FormFooter className={styles.footer}>
                  <FormRow>
                    <Button
                      type="primary"
                      onClick={ticketForm.submit}
                      disabled={isLoading || ticketId ? true : false}
                    >
                      Create
                    </Button>
                    <Button disabled={isLoading} onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                  </FormRow>
                </FormFooter>
              </Form>
            </div>
          ) : null}

          <div
            style={{
              width: "-webkit-fill-available",
              border: "1px solid #f4f4f4",
              height: "89vh",
              overflow: "scroll",
            }}
          >
            {ticketId ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "end",
                  width: "100%",
                  gap: "0.5rem",
                  background: "#fafafa",
                }}
              >
                <Button
                  type="primary"
                  disabled={isLoading}
                  onClick={() => {
                    if (ticketDetails && selectedDealer) {
                      generateInvoicePdf(ticketDetails);
                    } else {
                      console.error("Missing ticketDetails or selectedDealer");
                    }
                  }}
                >
                  Download <FilePdfOutlined />
                </Button>
                <Button disabled={isLoading} onClick={() => navigate(-1)}>
                  Back
                </Button>
              </div>
            ) : null}

            <DealerDetails
              selectedDealer={selectedDealer}
              ticketDetails={ticketDetails}
              spareDetails={spareDetails}
            />
          </div>
        </div>

        {/* Ticket created confirmation modal */}
        <CustomModal
          title="Confirmation"
          isOpen={isModalVisible}
          hideCancelButton={false}
          hideOkButton={false}
          okText={isTextCopied ? "Copied" : "Copy Request No."}
          onOk={async () => {
            await navigator.clipboard.writeText(serviceReqNo);
            setTextCopied(true);
          }}
          onCancel={() => {
            setModalVisible(false);
            navigate(-1);
          }}
        >
          <div>
            Ticket has been created successfully with service request no. #
            {serviceReqNo}.
          </div>
        </CustomModal>
      </div>
    </>
  );
}
